// @givmo/org-console — the save line a Profile → Branding view draws at its top right: what is
// saved, and Save. Branding's logos, colours and words and its housed QR code design both use it, so
// the tab has ONE save model: an edit shows "Unsaved changes" here, and only Save keeps it.

import { Button, Icon, cn, testIdProp } from '@givmo/design-system';
import { Check } from 'lucide-react';
import { PROFILE_COPY } from './profile-copy';

const COPY = PROFILE_COPY.branding;

export type OrgSaveLineState = 'saved' | 'unsaved' | 'saving';

export function OrgSaveLine({
  state,
  savedLabel = COPY.saved,
  canSave,
  onSave,
  testIdPrefix,
}: {
  state: OrgSaveLineState;
  /** What "nothing to save" says; Branding's form says "All changes saved". */
  savedLabel?: string;
  /** False holds Save back even with something unsaved (a design the server refused); true offers it
   *  even with nothing unsaved (a saved QR design the server can no longer use, which the standard
   *  replaces). */
  canSave: boolean;
  onSave: () => void;
  /** The line's ids: `<prefix>-save-state`, `<prefix>-save`. */
  testIdPrefix: string;
}) {
  return (
    <span className="ml-auto flex items-center gap-12">
      <span
        className={cn(
          'flex items-center gap-6 font-sans text-s13',
          state === 'unsaved' ? 'font-w600 text-text-strong' : 'text-text-tertiary',
        )}
        role="status"
        {...testIdProp(`${testIdPrefix}-save-state`)}
      >
        {state === 'saving' ? (
          COPY.saving
        ) : state === 'unsaved' ? (
          <>
            <span aria-hidden className="size-8 shrink-0 rounded-full bg-warning-500" />
            {COPY.unsaved}
          </>
        ) : (
          <>
            <Icon icon={Check} size={14} aria-hidden />
            {savedLabel}
          </>
        )}
      </span>
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        disabled={state === 'saving' || !canSave}
        loading={state === 'saving'}
        onClick={onSave}
        testId={`${testIdPrefix}-save`}
      >
        {COPY.save}
      </Button>
    </span>
  );
}
